import { ApiError, NetworkError, type ProblemDetails } from './api-error.ts';

export type ApiClientConfig = {
  baseUrl: string;
  getAccessToken: () => Promise<string | null>;
  fetchImpl?: typeof fetch;
  onUnauthorized?: () => void;
};

export type RequestOptions = Omit<RequestInit, 'body' | 'headers'> & {
  body?: unknown;
  headers?: Record<string, string>;
  idempotencyKey?: string;
  query?: Record<string, QueryValue | readonly QueryValue[] | undefined>;
};

export type QueryValue = string | number | boolean;

export type ApiClient = {
  request: <T>(path: string, options?: RequestOptions) => Promise<T>;
  get: <T>(path: string, options?: RequestOptions) => Promise<T>;
  post: <T>(path: string, body: unknown, options?: RequestOptions) => Promise<T>;
  put: <T>(path: string, body: unknown, options?: RequestOptions) => Promise<T>;
  patch: <T>(path: string, body: unknown, options?: RequestOptions) => Promise<T>;
  delete: <T>(path: string, options?: RequestOptions) => Promise<T>;
};

export const ACCEPT_HEADER = 'application/vnd.platform.v1+json';

export const newIdempotencyKey = (): string => crypto.randomUUID();

const buildUrl = (baseUrl: string, path: string, query: RequestOptions['query']): string => {
  const url = new URL(path, baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`);
  for (const [key, value] of Object.entries(query ?? {}))
    for (const item of value === undefined ? [] : [value].flat()) url.searchParams.append(key, String(item));
  return url.toString();
};

const parseProblem = async (response: Response): Promise<ProblemDetails> => {
  const text = await response.text();
  if (!text) return { status: response.status };
  try {
    return JSON.parse(text) as ProblemDetails;
  } catch {
    return { status: response.status, detail: text };
  }
};

export const createApiClient = (config: ApiClientConfig): ApiClient => {
  const fetchImpl = config.fetchImpl ?? fetch;

  const request = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
    const { body, headers, idempotencyKey, query, ...init } = options;
    const token = await config.getAccessToken();
    const finalHeaders: Record<string, string> = { Accept: ACCEPT_HEADER, ...headers };
    if (token) finalHeaders['Authorization'] = `Bearer ${token}`;
    if (body !== undefined) finalHeaders['Content-Type'] = 'application/json';
    if (idempotencyKey) finalHeaders['Idempotency-Key'] = idempotencyKey;

    let response: Response;
    try {
      response = await fetchImpl(buildUrl(config.baseUrl, path, query), {
        ...init,
        headers: finalHeaders,
        body: body === undefined ? null : JSON.stringify(body),
      });
    } catch (error) {
      throw new NetworkError(error);
    }

    if (!response.ok) {
      const problem = await parseProblem(response);
      if (response.status === 401) config.onUnauthorized?.();
      throw new ApiError(response.status, problem);
    }
    if (response.status === 204) return undefined as T;
    return (await response.json()) as T;
  };

  return {
    request,
    get: (path, options) => request(path, { ...options, method: 'GET' }),
    post: (path, body, options) => request(path, { ...options, method: 'POST', body }),
    put: (path, body, options) => request(path, { ...options, method: 'PUT', body }),
    patch: (path, body, options) => request(path, { ...options, method: 'PATCH', body }),
    delete: (path, options) => request(path, { ...options, method: 'DELETE' }),
  };
};
